import { useMemo } from "react";
import { useQuery } from "@apollo/client";
import TransactionsType from "enums/TransactionsType";
import { GET_TRANSACTIONS_FOR_DUPLICATE_CHECK } from "graphql/transaction";
import { FormikCreateExpense } from "utils/reimbursements/reimbursementExpenseFormat";
import {
  buildAmountSearchTerm,
  DuplicateCandidate,
  rankDuplicateCandidates,
  ScoredDuplicate,
} from "utils/reimbursements/expenseDuplicateMatch";

interface UseDuplicateExpenseCheckProps {
  expenseValues: FormikCreateExpense;
  enabled: boolean;
}

const useDuplicateExpenseCheck = ({
  expenseValues,
  enabled,
}: UseDuplicateExpenseCheckProps) => {
  const expenseType = expenseValues?.expenseType;
  const payToId = expenseValues?.payTo?.id;
  const amount = expenseValues?.amount;
  const editingId = expenseValues?.expenseId;

  const shouldRun =
    enabled &&
    !editingId &&
    !!payToId &&
    !!amount &&
    (expenseType === TransactionsType.CASH ||
      expenseType === TransactionsType.MILEAGE);

  const searchTerm = useMemo(() => buildAmountSearchTerm(amount), [amount]);

  const { data, loading } = useQuery(GET_TRANSACTIONS_FOR_DUPLICATE_CHECK, {
    variables: {
      type: expenseType ? [expenseType] : undefined,
      date: "last_30_days",
      cardHolder: payToId,
      searchTerm,
      first: 25,
    },
    skip: !shouldRun,
    fetchPolicy: "cache-and-network",
  });

  const potentialDuplicates: ScoredDuplicate[] = useMemo(() => {
    if (!shouldRun || !data?.transactions?.edges) return [];
    const candidates: DuplicateCandidate[] = data.transactions.edges
      .map((edge: any) => edge?.node)
      .filter(Boolean);
    return rankDuplicateCandidates(expenseValues, candidates, editingId);
  }, [shouldRun, data, expenseValues, editingId]);

  return {
    potentialDuplicates,
    loading: shouldRun && loading,
    hasDuplicates: potentialDuplicates.length > 0,
  };
};

export default useDuplicateExpenseCheck;
